import React, { useState } from "react";
import AppUtils from "@src/utils/appUtils";
import PropTypes from "prop-types";
import { BackButton, Loader } from "@src/components/atoms";
import { default as SectionContainer } from "@src/pages/sharedSections/SectionContainer";
import { useOperatorContext } from "@src/context/operatorContext";
import { useDCContext } from "@src/context/dcContext";
import SelfReportTable from "@src/components/tables/operator/SelfReportTable";
import SelfReportInfoCard from "@src/components/cards/operator/SelfReportInfoCard";
import OperatorHooks from "@src/context/hooks/operatorHooks";
import { useNavigate } from "react-router-dom";
import { trackActionActivity } from "@src/utils/userActivityTracker";
import ErrorPage from "@src/components/layout/ErrorPage";

const SelfReportingPage = () => {
  const { profile } = useOperatorContext();
  const { customer, behaviours } = profile;
  const dcContext = useDCContext();
  const { getDynamicReplacement } = dcContext;
  const sortedBehaviours = behaviours.map((b) => b.behaviourAcronym);
  const showPredictions = customer?.isPredictionsEnabled;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(false);

  const navigate = useNavigate();

  //Incomplete events
  const {
    data: { incompleteEvents } = {},
    isPending: incompleteEventsPending,
    error: incompleteEventsError,
  } = OperatorHooks.useOperatorIncompleteEvents(
    profile?.operator?.id,
    customer.industry,
  );

  const mutationCallback = (status) => {
    if (!!status) {
      navigate("/dashboard/operator/overview");
    } else {
      setError(true);
    }
    setIsSubmitting(false);
  };
  const mutation = OperatorHooks.useSaveSelfReport(
    mutationCallback,
    profile?.operator?.id,
    customer.industry,
  );

  const onSubmit = async (changes) => {
    setError(false);
    setIsSubmitting(true);
    try {
      const body = { report: { behaviours: [...changes] } };
      trackActionActivity("SELF_REPORT_SAVE", body);
      mutation.mutateAsync(body);
    } catch (e) {
      console.error(e);
      setError(true);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="relative flex md:flex-col items-center justify-center mx-4 mb-8 mt-4 md:h-14">
        <div className="md:absolute self-start mr-4">
          <BackButton trackingActionKey="SELF_REPORT_BACK_BUTTON" />
        </div>
        <h2 className="font-bold text-2xl md:text-3xl mx-16 text-center">
          {getDynamicReplacement(
            `captain__self_report__title${showPredictions ? "_predicted" : ""}`,
          )}
        </h2>
      </div>

      <div className="px-6 flex flex-col gap-4 mb-4">
        <SelfReportInfoCard
          showPredictions={showPredictions}
          dc={getDynamicReplacement}
        />
        {incompleteEventsPending || isSubmitting ? (
          <div className="min-h-120">
            <div className="w-full h-full flex items-center justify-center">
              <Loader size="2xl" />
            </div>
          </div>
        ) : incompleteEventsError ? (
          <ErrorPage pageName={"self-reporting"} />
        ) : (
          <SectionContainer shadow={false}>
            <SelfReportTable
              industry={customer?.industry}
              getDynamicContent={getDynamicReplacement}
              sortedBehaviours={sortedBehaviours}
              events={incompleteEvents}
              onChangeSubmit={onSubmit}
              showPredictions={
                customer.industry !== "shipping" &&
                !!customer?.enabledPredictions
              }
            />
            {error && (
              <div className="mt-4 text-md text-center text-red-500">
                We couldn't save your changes. Please try again later.
              </div>
            )}
          </SectionContainer>
        )}
      </div>
    </div>
  );
};

export const selfReportingPageControls = {};

SelfReportingPage.propTypes = AppUtils.getPropTypeFromControls(
  selfReportingPageControls,
);

export default SelfReportingPage;
